<script setup lang="ts">
import { useI18n } from '@/i18n';
import { ssoErrorMessageKey } from '@/lib/auth-api-error-codes';
import { createLoginSchema, type LoginFormValues } from '@/schemas/login';
import { useAuthStore } from '@/stores/auth';
import { useAuthFlow, type SsoSuccessPayload } from '@/use/authFlow';
import { Alert, Button, Form, Input, LayoutGap } from '@dabble/quillui';
import AuthLayout from '@/components/AuthLayout/AuthLayout.vue';
import AuthInviteBanner from '@/components/AuthInviteBanner/AuthInviteBanner.vue';
import AuthTemplateBanner from '@/components/AuthTemplateBanner/AuthTemplateBanner.vue';
import AuthSsoButtons from '@/components/AuthSsoButtons/AuthSsoButtons.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, ref } from 'vue';

const { t, te } = useI18n();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}

const authStore = useAuthStore();
const { resolveSignupContext, completeAuth } = useAuthFlow();

const error = ref<string | undefined>(undefined);

/** A first-timer can click Google here too — carry the signup intent through. */
const ssoSignUpOptions = computed(() => resolveSignupContext().signUpOptions);

const rawLoginSchema = computed(() => createLoginSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawLoginSchema.value));

const { handleSubmit, defineField, values } = useForm<LoginFormValues>({
  validationSchema,
  initialValues: {
    email: '',
    password: '',
  },
});

const isValid = computed(() =>
  rawLoginSchema.value.isValidSync({
    email: values.email ?? '',
    password: values.password ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [email, emailField] = defineField('email', fieldOpts);
const [password, passwordField] = defineField('password', fieldOpts);

const errorMessage = computed(() => {
  const raw = error.value ?? authStore.error;
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
});

const onSubmit = handleSubmit(async (values: LoginFormValues) => {
  error.value = undefined;
  try {
    const response = await authStore.login(values.email, values.password);
    if (response) {
      await completeAuth(response.user, false);
    }
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  }
});

/** Post-auth routing is decided by `isNewUser`, not by which view hosted the button. */
async function onSsoSuccess(payload: SsoSuccessPayload) {
  error.value = undefined;
  try {
    await completeAuth(payload.user, payload.isNewUser);
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  }
}

function onSsoError(code: string) {
  error.value = ssoErrorMessageKey(code);
}
</script>

<template>
  <AuthLayout>
    <AuthInviteBanner />
    <AuthTemplateBanner context="login" />
    <Form v-model:dirty="isDirty" :valid="isValid" :actions="false" @submit="onSubmit">
      <LayoutGap direction="column" size="medium" align="center" class="login__form-contents">
        <div v-if="errorMessage" class="login__alert" role="alert" aria-live="assertive" aria-atomic="true">
          <Alert variant="danger">{{ errorMessage }}</Alert>
        </div>
        <AuthSsoButtons mode="signin" :sign-up-options="ssoSignUpOptions" @success="onSsoSuccess" @error="onSsoError" />
        <LayoutGap direction="column" align="center" size="large" class="login__form-stack">
          <LayoutGap direction="column" size="small">
            <Input
              id="email"
              v-model="email"
              class="auth-field"
              v-bind="emailField"
              :aria-label="t('email')"
              type="text"
              required
              size="large"
              autocapitalize="off"
              :disabled="authStore.signingIn"
              :placeholder="t('signup_placeholder_email')"
            />
            <Input
              id="password"
              v-model="password"
              class="auth-field"
              v-bind="passwordField"
              :aria-label="t('password')"
              type="password"
              :placeholder="t('signup_placeholder_password')"
              required
              autocapitalize="off"
              size="large"
              :disabled="authStore.signingIn"
              autocomplete="current-password"
            >
              <template #note></template>
            </Input>
          </LayoutGap>
          <LayoutGap direction="column" align="center">
            <Button
              class="auth-submit"
              type="submit"
              :disabled="authStore.signingIn || !isValid"
              fill-width
              size="large"
            >
              {{ authStore.signingIn ? t('auth_logging_in') : t('auth_log_in') }}
            </Button>

            <router-link to="/account/lost-password">{{ t('auth_forgot_password') }}</router-link>
          </LayoutGap>
        </LayoutGap>
      </LayoutGap>
    </Form>

    <template #footer>
      <p class="login__signup-link">
        {{ t('auth_no_account') }}
        <router-link to="/auth/signup">{{ t('auth_sign_up') }}</router-link>
      </p>
    </template>
  </AuthLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/auth-form.scss';

.login {
  &__signup-link {
    margin: 0;
  }
}
</style>
